// In-depth CSS lessons (Codeazy lesson standard).

export const cssLessons = [
  {
    slug: "box-model",
    title: "The Box Model and CSS Units",
    summary: "Every element is a box. Learn how its size is calculated, why margins collapse, and which units to use when.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "“Why is my 300px card 342px wide?” “Where did this gap come from?” “Why does my layout overflow on mobile?” Almost every layout bug starts with the box model or with the wrong unit. Master these and CSS stops feeling random." },

      { type: "h2", text: "The mental model: a framed photo" },
      { type: "p", text: "Picture a framed photo on a wall. The **photo** is the content. The **mount board** around it is the padding. The **frame** is the border. The **empty wall** between it and the next frame is the margin. The background colour fills the content and the padding, but never the margin." },
      { type: "code", language: "text", code: `┌──────────────── margin (transparent) ────────────────┐
│  ┌──────────────── border ─────────────────────────┐  │
│  │  ┌──────────── padding ─────────────────────┐   │  │
│  │  │                                          │   │  │
│  │  │              content                     │   │  │
│  │  │        (width × height)                  │   │  │
│  │  └──────────────────────────────────────────┘   │  │
│  └─────────────────────────────────────────────────┘  │
└───────────────────────────────────────────────────────┘` },

      { type: "h2", text: "content-box vs border-box" },
      { type: "p", text: "By default (`box-sizing: content-box`), `width` sets only the content. Padding and border are added on top:" },
      { type: "code", language: "css", code: `.card {
  width: 300px;
  padding: 20px;
  border: 1px solid #ddd;
}
/* Rendered width = 300 + 20 + 20 + 1 + 1 = 342px */` },
      { type: "p", text: "With `border-box`, `width` includes padding and border, so the card is exactly 300px. Almost every modern project sets this globally:" },
      { type: "code", language: "css", code: `*,
*::before,
*::after {
  box-sizing: border-box;
}` },
      { type: "table", headers: ["box-sizing", "width: 300px means", "Final width with 20px padding and 1px border"], rows: [
        ["`content-box` (default)", "Content is 300px", "342px"],
        ["`border-box`", "Content + padding + border = 300px", "300px (content shrinks to 258px)"]
      ] },

      { type: "h2", text: "Margin, padding and shorthand" },
      { type: "code", language: "css", code: `padding: 16px;                 /* all four sides */
padding: 8px 16px;             /* top/bottom, left/right */
padding: 8px 16px 24px;        /* top, left/right, bottom */
padding: 8px 16px 24px 32px;   /* top, right, bottom, left (clockwise) */

margin: 0 auto;                /* centre a block horizontally (needs a width) */
margin-inline: auto;           /* modern logical equivalent */
padding-block: 2rem;           /* top and bottom only */` },
      { type: "callout", variant: "tip", text: "Remember the shorthand order with “TRouBLe”: Top, Right, Bottom, Left." },

      { type: "h2", text: "Margin collapsing" },
      { type: "p", text: "Vertical margins between block elements don’t add up. The larger one wins:" },
      { type: "code", language: "css", code: `h2 { margin-bottom: 24px; }
p  { margin-top: 16px; }
/* Gap between h2 and p = 24px, not 40px */` },
      { type: "p", text: "Collapsing happens only with **vertical** margins on **block** elements in normal flow. It doesn’t happen inside flex or grid containers, with padding or borders in between, or with horizontal margins. A child’s top margin can also “leak” out of a parent that has no padding or border, which is a common source of mystery gaps." },
      { type: "callout", variant: "note", text: "Modern layouts often avoid margin surprises completely by using `gap` on a flex or grid parent instead of margins on the children." },

      { type: "h2", text: "display: block, inline and inline-block" },
      { type: "table", headers: ["", "block", "inline", "inline-block"], rows: [
        ["Starts on a new line", "Yes", "No", "No"],
        ["Respects `width` / `height`", "Yes", "No", "Yes"],
        ["Vertical padding and margin push other content away", "Yes", "No (padding paints but doesn’t push lines)", "Yes"],
        ["Typical use", "Sections, cards", "Text, links", "Buttons and badges inside text"]
      ] },

      { type: "h2", text: "CSS units: which one when?" },
      { type: "table", headers: ["Unit", "Relative to", "Use it for"], rows: [
        ["`px`", "Nothing (fixed)", "Borders, shadows, small fixed details"],
        ["`rem`", "Root (`html`) font size, usually 16px", "Font sizes, spacing and most sizes, because it respects user font settings"],
        ["`em`", "The element’s own font size", "Padding that should scale with a component’s text, such as buttons"],
        ["`%`", "The parent’s size", "Fluid widths"],
        ["`vw` / `vh`", "1% of the viewport width or height", "Full-screen sections, fluid type"],
        ["`dvh` / `svh` / `lvh`", "Dynamic, small or large viewport height", "Mobile full-height sections, where browser bars change the height"],
        ["`ch`", "Width of the “0” character", "Readable line length: `max-width: 65ch`"],
        ["`fr`", "Free space in a grid", "Grid columns and rows"]
      ] },
      { type: "code", language: "css", code: `html { font-size: 100%; }          /* respect the user's browser setting (16px default) */

.article {
  max-width: 65ch;                  /* comfortable reading width */
  padding: 1.5rem;                  /* 24px at default settings */
  font-size: clamp(1rem, 0.9rem + 0.5vw, 1.25rem);  /* fluid text */
}

.button {
  font-size: 1rem;
  padding: 0.75em 1.25em;           /* scales if the button's font-size changes */
}` },
      { type: "callout", variant: "warning", text: "Avoid `font-size` in px for body text. Users who set a larger default font size in their browser, often for vision reasons, won’t get bigger text. `rem` respects that setting." },

      { type: "h2", text: "Overflow" },
      { type: "code", language: "css", code: `.code-block   { overflow-x: auto; }    /* scroll sideways only when needed */
.avatar       { overflow: hidden; border-radius: 50%; }  /* clip to the circle */
.title        {                       /* single-line ellipsis */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}` },

      { type: "h2", text: "Debugging boxes" },
      { type: "ul", items: [
        "In Chrome DevTools, select an element and look at the **box model diagram** in the Computed tab. It shows the exact content, padding, border and margin.",
        "Temporarily add `outline: 1px solid red` to suspicious elements. Unlike `border`, an outline doesn’t change the layout.",
        "To find what is causing horizontal scroll on mobile, try `* { outline: 1px solid red; }` and look for the box that sticks out."
      ] },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Fixed width breaks mobile", bad: `.container {
  width: 1200px;
}`, goodLabel: "Fluid with a cap", good: `.container {
  width: 100%;
  max-width: 1200px;
  margin-inline: auto;
  padding-inline: 1rem;
}`, text: "A fixed width wider than the screen causes horizontal scrolling on phones. `max-width` lets the container shrink." },
      { type: "compare", badLabel: "100vh on mobile", bad: `.hero {
  height: 100vh;
}
/* hidden behind the mobile
   browser's address bar */`, goodLabel: "Dynamic viewport units", good: `.hero {
  min-height: 100vh;    /* fallback */
  min-height: 100dvh;   /* tracks the visible area */
}` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "Explain the CSS box model.", answer: ["Every element is a rectangular box made of content, padding, border and margin, from the inside out. With the default `content-box`, `width` and `height` apply to the content only, and padding and border are added. With `border-box`, they include padding and border, which makes sizing predictable."] },
      { type: "qa", question: "What is margin collapsing?", answer: ["When vertical margins of adjacent block elements touch, they combine into a single margin equal to the larger one, instead of adding up. It also happens between a parent and its first or last child when nothing separates them. It doesn’t occur in flex or grid layouts, or with horizontal margins."] },
      { type: "qa", question: "What is the difference between `rem` and `em`?", answer: ["`rem` is relative to the root element’s font size, so it is consistent everywhere. `em` is relative to the current element’s font size, so it compounds when nested: a 1.2em inside a 1.2em is 1.44 times the base. Use `rem` for most sizing, and `em` when something should scale with its own component’s text."] },
      { type: "qa", question: "How do you centre a block element horizontally?", answer: ["Give it a width or max-width and set `margin-inline: auto` (or `margin: 0 auto`). For centring in both directions, use flexbox or grid on the parent: `display: grid; place-items: center;`."] },
      { type: "qa", question: "Why use `outline` instead of `border` for debugging?", answer: ["Outlines are drawn outside the box and don’t take up space, so adding one doesn’t shift the layout you are trying to debug."] },

      { type: "exercise", title: "Build a responsive card", task: "Style a `.card` that is never wider than 360px but shrinks on small screens, is centred horizontally, has 1.5rem of padding included in its width, a 1px border, a rounded corner of 16px and a title that truncates to one line with an ellipsis.", language: "css", solution: `*, *::before, *::after { box-sizing: border-box; }

.card {
  width: 100%;
  max-width: 360px;
  margin-inline: auto;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.card__title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}`, explanation: "`border-box` keeps the padding inside the 360px. `width: 100%` with `max-width` lets it shrink on small screens, and `margin-inline: auto` centres it. The ellipsis needs all three properties: `nowrap`, `overflow: hidden` and `text-overflow: ellipsis`." },

      { type: "summary", items: [
        "Every element is content, padding, border and margin.",
        "Set `box-sizing: border-box` globally so `width` means what you expect.",
        "Vertical block margins collapse, so `gap` in flex or grid avoids the surprise.",
        "Use `rem` for most sizes, `em` for component-relative padding, `ch` for line length and `dvh` for mobile heights.",
        "Prefer `max-width` over fixed widths, and debug with DevTools and outlines."
      ] }
    ]
  },

  {
    slug: "cascade-and-specificity",
    title: "The Cascade, Specificity and Inheritance",
    summary: "Why some CSS rules win and others don’t, and how to write CSS that doesn’t need !important.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "“My CSS isn’t working” usually means another rule is winning. Understanding how browsers resolve conflicts, through the cascade, specificity and inheritance, turns guesswork into a quick diagnosis and stops the `!important` arms race that makes large codebases unmaintainable." },

      { type: "h2", text: "The mental model: a tie-breaker tournament" },
      { type: "p", text: "When several rules set the same property on the same element, the browser runs a tournament. Each round is a tie-breaker. Only if two rules are tied does the next round decide:" },
      { type: "ol", items: [
        "**Origin and importance:** user-agent defaults < your styles < your `!important` styles.",
        "**Cascade layers:** for normal styles, a later `@layer` beats an earlier one, and unlayered styles beat all layers.",
        "**Specificity:** the more specific selector wins.",
        "**Source order:** if everything else is equal, the rule that appears last wins."
      ] },

      { type: "h2", text: "Calculating specificity" },
      { type: "p", text: "Specificity is a score with three columns, compared from left to right like version numbers: **(IDs, classes/attributes/pseudo-classes, elements/pseudo-elements)**." },
      { type: "table", headers: ["Selector", "Score", "Why"], rows: [
        ["`p`", "(0, 0, 1)", "One element"],
        ["`.card`", "(0, 1, 0)", "One class"],
        ["`.card p`", "(0, 1, 1)", "One class and one element"],
        ["`nav a:hover`", "(0, 1, 2)", "One pseudo-class and two elements"],
        ["`input[type=\"email\"]`", "(0, 1, 1)", "An attribute counts like a class"],
        ["`#signup .btn`", "(1, 1, 0)", "One ID and one class"],
        ["`style=\"...\"`", "Beats any selector", "Inline styles"],
        ["`*`", "(0, 0, 0)", "The universal selector adds nothing"]
      ] },
      { type: "callout", variant: "note", text: "One ID beats any number of classes: (1, 0, 0) > (0, 15, 0). Compare the columns from the left and stop at the first difference." },
      { type: "code", language: "css", code: `/* Which colour is the button? */
#checkout button   { color: red; }    /* (1, 0, 1) */
.btn.btn-primary   { color: blue; }   /* (0, 2, 0) */
button.btn         { color: green; }  /* (0, 1, 1) */

/* <div id="checkout"><button class="btn btn-primary">Pay</button></div> */`, output: `red, because (1, 0, 1) has an ID and beats both class-based selectors` },

      { type: "h3", text: "Modern selectors and specificity" },
      { type: "table", headers: ["Selector", "Specificity rule"], rows: [
        ["`:is(.a, #b)`", "Takes the specificity of its **most** specific argument, here (1, 0, 0)"],
        ["`:not(.a)`", "Same as `:is`: its most specific argument"],
        ["`:where(.a, #b)`", "Always **zero**, which is great for overridable base styles"],
        ["`:has(img)`", "Its most specific argument"]
      ] },
      { type: "code", language: "css", code: `/* Base styles anyone can override with a single class */
:where(.prose) a { color: #2563eb; }   /* (0, 0, 1) */

.footer-link { color: gray; }         /* (0, 1, 0): wins easily */` },

      { type: "h2", text: "Inheritance" },
      { type: "p", text: "Some properties pass down from parent to children automatically, which is why setting `font-family` on `body` styles the whole page." },
      { type: "table", headers: ["Usually inherited", "Not inherited"], rows: [
        ["`color`, `font-*`, `line-height`, `text-align`, `letter-spacing`, `visibility`, `cursor`, `list-style`", "`margin`, `padding`, `border`, `background`, `width`, `height`, `display`, `position`"]
      ] },
      { type: "code", language: "css", code: `button {
  font: inherit;       /* form controls don't inherit fonts by default */
  color: inherit;
}

.card a {
  color: currentColor; /* use the element's current text colour */
}

.reset-me {
  all: unset;          /* remove every inherited and default value */
}` },
      { type: "callout", variant: "tip", text: "Inherited values have no specificity at all. Any rule that targets the element directly, even `*`, beats a value inherited from a parent." },

      { type: "h2", text: "Cascade layers" },
      { type: "p", text: "`@layer` lets you decide the order of whole groups of styles up front, so a utility class can beat a component style regardless of specificity:" },
      { type: "code", language: "css", code: `@layer reset, base, components, utilities;

@layer components {
  #sidebar .nav-link { padding: 1rem; }   /* (1, 1, 0) */
}

@layer utilities {
  .p-0 { padding: 0; }                     /* (0, 1, 0), but its layer is later */
}

/* <a id=... class="nav-link p-0">: padding is 0 */` },

      { type: "h2", text: "!important" },
      { type: "p", text: "`!important` jumps a declaration to a higher-priority round. It solves one conflict but creates the next one, because the only way to beat it is another `!important` with higher specificity." },
      { type: "ul", items: [
        "Acceptable: utility classes designed to always win, overriding third-party inline styles, and user-preference rules such as `prefers-reduced-motion` overrides.",
        "Not acceptable: fixing a specificity problem you don’t understand."
      ] },

      { type: "h2", text: "Writing CSS that scales" },
      { type: "ul", items: [
        "Style mostly with **single classes**, keeping specificity low and flat.",
        "Avoid IDs in selectors. Use them for anchors and JavaScript.",
        "Avoid deep nesting such as `.page .main .card .title span`.",
        "Use a naming convention such as BEM (`.card`, `.card__title`, `.card--featured`) so classes don’t clash.",
        "In React projects, CSS Modules, Tailwind or CSS-in-JS scope styles to components automatically."
      ] },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Specificity war", bad: `.btn { background: blue; }
#app .header .btn { background: red; }
.btn-ghost { background: none !important; }
#app .btn-ghost { background: none !important; }`, goodLabel: "Flat, predictable classes", good: `.btn { background: blue; }
.btn--danger { background: red; }
.btn--ghost { background: none; }` },
      { type: "compare", badLabel: "Over-qualified selector", bad: `div.card ul.list li.item a.link {
  color: teal;
}`, goodLabel: "One class", good: `.card-link {
  color: teal;
}` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "How does the browser decide which CSS rule applies?", answer: ["Through the cascade. It compares origin and importance first, then cascade layers, then specificity, and finally source order, where the last rule wins. Inherited values only apply if no rule targets the element directly."] },
      { type: "qa", question: "How is specificity calculated?", answer: ["As three counts: IDs, then classes, attributes and pseudo-classes, then elements and pseudo-elements. They are compared from left to right. Inline styles beat all selectors, `*` and `:where()` add nothing, and `:is()`, `:not()` and `:has()` take their most specific argument."] },
      { type: "qa", question: "What is the difference between `:is()` and `:where()`?", answer: ["They match the same elements, but `:where()` always has zero specificity while `:is()` takes the specificity of its most specific argument. `:where()` is ideal for default styles that should be easy to override."] },
      { type: "qa", question: "Why is `!important` considered bad practice?", answer: ["It bypasses normal specificity, so the only way to override it is another `!important`, which leads to escalating overrides and unpredictable styles. Use it only in deliberate cases such as utility classes or overriding third-party inline styles."] },
      { type: "qa", question: "Which properties are inherited?", answer: ["Mostly text-related ones: `color`, the `font` properties, `line-height`, `text-align`, `letter-spacing`, `visibility` and `cursor`. Box and layout properties such as `margin`, `padding`, `border`, `background` and `display` are not inherited."] },

      { type: "exercise", title: "Who wins?", task: "For the HTML `<ul id=\"menu\"><li class=\"item active\"><a href=\"#\" class=\"link\">Home</a></li></ul>`, work out the final colour of the link.", language: "css", starter: `#menu a          { color: gray; }
.item.active .link { color: green; }
ul li a:hover    { color: orange; }   /* assume not hovered */
a.link           { color: purple !important; }
.link            { color: blue; }`, solution: `/* Scores (ignoring !important):
   #menu a             (1, 0, 1)
   .item.active .link  (0, 3, 0)
   a.link              (0, 1, 1)
   .link               (0, 1, 0)

   !important is a higher-priority round, so a.link wins
   before specificity is even compared. */`, output: `purple`, explanation: "Without the `!important`, `#menu a` would win because it has an ID, giving gray. This shows why `!important` is so disruptive: it overrides a far more specific rule." },

      { type: "summary", items: [
        "Conflicts are resolved by origin and importance, then layers, then specificity, then source order.",
        "Specificity is (IDs, classes, elements), compared from left to right. One ID beats any number of classes.",
        "`:where()` has zero specificity, while `:is()`, `:not()` and `:has()` take their strongest argument.",
        "Text properties inherit and box properties don’t. Inherited values lose to any direct rule.",
        "Keep selectors flat with single classes, and avoid IDs and `!important` for styling."
      ] }
    ]
  },

  {
    slug: "flexbox",
    title: "Flexbox",
    summary: "One-dimensional layouts: navbars, toolbars, card rows, centring and the flex: 1 mystery explained.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Flexbox is the everyday layout tool. Navbars, buttons with icons, card footers, form rows and “centre this div” are all flexbox. Understanding the axes and how items grow and shrink fixes most layout frustration." },

      { type: "h2", text: "The mental model: beads on a string" },
      { type: "p", text: "A flex container is a **string**, and its children are **beads**. The string has a direction, the **main axis**, and the beads line up along it. `justify-content` slides the beads along the string, and `align-items` moves them up and down across it, along the **cross axis**." },
      { type: "code", language: "text", code: `flex-direction: row (default)

 main axis ───────────────────────────────▶
 ┌─────────────────────────────────────────┐  ▲
 │ [item 1]  [item 2]  [item 3]            │  │ cross axis
 └─────────────────────────────────────────┘  ▼

flex-direction: column → the axes swap: main runs top to bottom` },

      { type: "h2", text: "Container properties" },
      { type: "table", headers: ["Property", "Common values", "What it does"], rows: [
        ["`display`", "`flex`, `inline-flex`", "Turns on flexbox for the direct children"],
        ["`flex-direction`", "`row`, `column`, `row-reverse`", "Sets the main axis"],
        ["`justify-content`", "`flex-start`, `center`, `space-between`, `space-around`, `space-evenly`", "Alignment along the main axis"],
        ["`align-items`", "`stretch` (default), `center`, `flex-start`, `flex-end`, `baseline`", "Alignment along the cross axis"],
        ["`flex-wrap`", "`nowrap` (default), `wrap`", "Whether items move onto new lines"],
        ["`gap`", "`1rem`, `8px 16px`", "Space between items, with no margin hacks"],
        ["`align-content`", "Same values as `justify-content`", "Spacing between wrapped lines"]
      ] },

      { type: "h2", text: "Everyday patterns" },
      { type: "h3", text: "Navbar: logo left, links right" },
      { type: "code", language: "css", code: `.navbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}` },
      { type: "h3", text: "Perfect centring" },
      { type: "code", language: "css", code: `.center {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100dvh;
}` },
      { type: "h3", text: "Push one item to the end" },
      { type: "code", language: "css", code: `.toolbar { display: flex; gap: 0.5rem; }
.toolbar .logout { margin-left: auto; }   /* takes all the free space before it */` },
      { type: "h3", text: "Card footer stuck to the bottom" },
      { type: "code", language: "css", code: `.card {
  display: flex;
  flex-direction: column;
}
.card__footer {
  margin-top: auto;   /* footer aligns across cards of different heights */
}` },
      { type: "h3", text: "Media object: icon plus text" },
      { type: "code", language: "css", code: `.media {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}
.media__icon { flex-shrink: 0; }   /* don't let the icon get squashed */
.media__body { flex: 1; min-width: 0; }` },

      { type: "h2", text: "Item properties: grow, shrink and basis" },
      { type: "p", text: "Each item has three sizing properties, usually set together with the `flex` shorthand:" },
      { type: "table", headers: ["Property", "Question it answers", "Default"], rows: [
        ["`flex-basis`", "What size should I start at before sharing space?", "`auto` (my content or width)"],
        ["`flex-grow`", "If there is spare space, how much of it do I take?", "`0` (don’t grow)"],
        ["`flex-shrink`", "If space is short, how much do I give up?", "`1` (shrink evenly)"]
      ] },
      { type: "table", headers: ["Shorthand", "Expands to", "Meaning"], rows: [
        ["`flex: 1`", "`1 1 0%`", "Grow and shrink equally from zero, so equal-width columns"],
        ["`flex: auto`", "`1 1 auto`", "Grow and shrink from content size, so bigger content gets more"],
        ["`flex: none`", "`0 0 auto`", "Fixed at content size"],
        ["`flex: 0 0 240px`", "", "A fixed 240px sidebar"]
      ] },
      { type: "code", language: "css", code: `.layout { display: flex; gap: 1.5rem; }
.sidebar { flex: 0 0 240px; }   /* fixed */
.content { flex: 1; }           /* takes the rest */` },
      { type: "h3", text: "How grow is distributed" },
      { type: "p", text: "Container: 600px. Items have a basis of 100px each (300px used), so 300px is free. With `flex-grow` values of 1, 2 and 0, the free space is split 1:2:0, so the items get +100px, +200px and +0px." },
      { type: "code", language: "text", code: `item A: 100 + 100 = 200px   (grow 1)
item B: 100 + 200 = 300px   (grow 2)
item C: 100 +   0 = 100px   (grow 0)` },

      { type: "h2", text: "The min-width: 0 gotcha" },
      { type: "p", text: "Flex items won’t shrink below their content’s minimum size by default (`min-width: auto`). A long URL or a wide code block can then push the layout off-screen. The fix is `min-width: 0` on the flex item:" },
      { type: "compare", badLabel: "Content overflows", bad: `.chat { display: flex; }
.chat__message { flex: 1; }
/* a long unbroken URL stretches
   the message past the screen */`, goodLabel: "Item allowed to shrink", good: `.chat { display: flex; }
.chat__message {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
}` },

      { type: "h2", text: "Wrapping rows of cards" },
      { type: "code", language: "css", code: `.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Cards that wrap, each at least 250px */
.cards { display: flex; flex-wrap: wrap; gap: 1rem; }
.cards > * { flex: 1 1 250px; }` },
      { type: "callout", variant: "note", text: "With wrapping flex cards, the last row stretches to fill the width, so two cards on the final row become wider than the others. If every column must stay equal, that is a job for CSS Grid." },

      { type: "h2", text: "order and alignment of single items" },
      { type: "code", language: "css", code: `.item--featured { order: -1; }          /* move it visually first */
.item--bottom   { align-self: flex-end; } /* override align-items for one item */` },
      { type: "callout", variant: "warning", text: "`order` changes only the visual order. Keyboard tab order and screen readers still follow the HTML order, so don’t use `order` for meaningful content reordering." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between `justify-content` and `align-items`?", answer: ["`justify-content` aligns items along the main axis, which is horizontal for `row`. `align-items` aligns them along the cross axis, which is vertical for `row`. If you switch to `flex-direction: column`, their visual directions swap."] },
      { type: "qa", question: "What does `flex: 1` mean?", answer: ["It is shorthand for `flex-grow: 1; flex-shrink: 1; flex-basis: 0%`. Items start from zero and share all the space equally, which gives equal-width columns regardless of content."] },
      { type: "qa", question: "Why is my flex item overflowing its container?", answer: ["Flex items default to `min-width: auto`, so they won’t shrink below their content’s minimum width, for example a long word or URL, a wide image or a code block. Set `min-width: 0` (or `overflow: hidden`) on the item so it can shrink."] },
      { type: "qa", question: "When would you use flexbox instead of grid?", answer: ["Flexbox is for one-dimensional layouts where content size drives the layout: a row of buttons, a navbar or tags that wrap. Grid is for two-dimensional layouts where you define rows and columns: page layouts and card grids with aligned columns."] },
      { type: "qa", question: "How do you push the last item in a flex row to the right?", answer: ["Give it `margin-left: auto`. Auto margins in flexbox absorb all remaining free space on that side."] },

      { type: "exercise", title: "Build a pricing card row", task: "Create a `.pricing` row of cards that sit side by side on wide screens and wrap on narrow ones, with a 1.5rem gap. Each `.plan` card should be at least 260px wide, grow to share space, and have its `.plan__cta` button stuck to the bottom even when the cards have different amounts of text.", language: "css", solution: `.pricing {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.plan {
  flex: 1 1 260px;          /* grow, shrink, start at 260px */
  display: flex;
  flex-direction: column;   /* nested flex for the card's content */
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.plan__cta {
  margin-top: auto;         /* stick to the bottom */
}`, explanation: "The outer flex container handles the row and wrapping. Each card is also a column flex container, so `margin-top: auto` on the button absorbs the leftover space and lines the buttons up across cards." },

      { type: "summary", items: [
        "Flexbox lays items along a main axis. `justify-content` works on the main axis and `align-items` on the cross axis.",
        "Use `gap` for spacing and `flex-wrap: wrap` for responsive rows.",
        "`flex: 1` gives equal shares, and `flex: 0 0 240px` gives a fixed size.",
        "Auto margins push items apart, which is great for footers and “last item right”.",
        "Add `min-width: 0` when flex children overflow, and don’t use `order` for meaningful reordering."
      ] }
    ]
  },

  {
    slug: "grid",
    title: "CSS Grid",
    summary: "Two-dimensional layouts: page structures, responsive card grids without media queries, and named areas.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Before Grid, page layouts were built with floats and hacks. Grid lets you describe rows and columns directly, build responsive galleries in one line and rearrange whole layouts at different breakpoints. It is the right tool whenever you think in both rows and columns." },

      { type: "h2", text: "The mental model: a spreadsheet" },
      { type: "p", text: "A grid container is a **spreadsheet**. You define the columns and rows, and items drop into cells automatically, one per cell, left to right and top to bottom. You can also tell an item to span several cells, or put it at an exact address such as “columns 1 to 3, row 2”." },
      { type: "code", language: "text", code: `      line 1     line 2     line 3     line 4
        │          │          │          │
        ▼          ▼          ▼          ▼
 row 1  ┌──────────┬──────────┬──────────┐
        │  cell    │  cell    │  cell    │
 row 2  ├──────────┼──────────┼──────────┤
        │  cell    │  cell    │  cell    │
        └──────────┴──────────┴──────────┘
  3 columns = 4 column lines (items are placed between lines)` },

      { type: "h2", text: "Defining a grid" },
      { type: "code", language: "css", code: `.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);  /* 3 equal columns */
  gap: 1.5rem;                            /* row and column gap */
}

.layout {
  display: grid;
  grid-template-columns: 240px 1fr;       /* fixed sidebar, fluid content */
  grid-template-rows: auto 1fr auto;      /* header, content, footer */
  min-height: 100dvh;
}` },
      { type: "table", headers: ["Value", "Meaning"], rows: [
        ["`1fr`", "One fraction of the remaining free space"],
        ["`repeat(4, 1fr)`", "Four equal columns"],
        ["`minmax(200px, 1fr)`", "At least 200px, at most one fraction"],
        ["`auto`", "Sized to the content"],
        ["`min-content` / `max-content`", "As narrow as possible, or as wide as the content wants"],
        ["`fit-content(300px)`", "Content size, capped at 300px"]
      ] },

      { type: "h2", text: "The responsive grid in one line" },
      { type: "code", language: "css", code: `.gallery {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1rem;
}` },
      { type: "p", text: "Read it as: “fit as many columns as possible, each at least 250px, then share the leftover space equally.” On a 1100px container you get 4 columns, on 600px you get 2, and on a phone you get 1. No media queries are needed, and unlike wrapping flexbox, every column stays the same width." },
      { type: "table", headers: ["", "auto-fill", "auto-fit"], rows: [
        ["Creates empty columns if items run out", "Yes, so items keep their size", "No: empty tracks collapse, so items stretch"],
        ["Best for", "Galleries where cards shouldn’t stretch", "A few items that should fill the row"]
      ] },

      { type: "h2", text: "Placing and spanning items" },
      { type: "code", language: "css", code: `.featured {
  grid-column: 1 / 3;       /* from line 1 to line 3, so 2 columns */
  grid-row: span 2;         /* 2 rows tall */
}

.full-width {
  grid-column: 1 / -1;      /* first line to last line, so the full width */
}` },

      { type: "h2", text: "Named areas: draw your layout" },
      { type: "code", language: "css", code: `.page {
  display: grid;
  grid-template-columns: 260px 1fr;
  grid-template-rows: auto 1fr auto;
  grid-template-areas:
    "header  header"
    "sidebar main"
    "footer  footer";
  min-height: 100dvh;
}

.page > header { grid-area: header; }
.page > aside  { grid-area: sidebar; }
.page > main   { grid-area: main; }
.page > footer { grid-area: footer; }

/* Mobile: stack everything by redrawing the map */
@media (max-width: 768px) {
  .page {
    grid-template-columns: 1fr;
    grid-template-areas:
      "header"
      "main"
      "sidebar"
      "footer";
  }
}` },
      { type: "callout", variant: "tip", text: "Named areas are the most readable way to build page layouts. The CSS looks like a drawing of the page, and changing breakpoints only means redrawing the map." },

      { type: "h2", text: "Alignment in grid" },
      { type: "table", headers: ["Property", "Aligns"], rows: [
        ["`justify-items` / `align-items`", "Every item inside its cell (horizontally / vertically)"],
        ["`place-items: center`", "Both at once, for centring"],
        ["`justify-self` / `align-self`", "One item inside its cell"],
        ["`justify-content` / `align-content`", "The whole grid inside the container, when the tracks are smaller than the container"]
      ] },
      { type: "code", language: "css", code: `/* The shortest centring snippet in CSS */
.center {
  display: grid;
  place-items: center;
}` },

      { type: "h2", text: "Auto-flow and dense packing" },
      { type: "code", language: "css", code: `.masonry-ish {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  grid-auto-rows: 120px;       /* height of rows created automatically */
  grid-auto-flow: dense;       /* backfill holes left by spanning items */
}
.masonry-ish .wide { grid-column: span 2; }
.masonry-ish .tall { grid-row: span 2; }` },
      { type: "callout", variant: "warning", text: "`dense` can move items out of their HTML order to fill gaps, so keyboard order no longer matches what users see. Use it only for galleries where order doesn’t matter." },

      { type: "h2", text: "Subgrid: aligning nested content" },
      { type: "p", text: "Cards in a row often have titles of different lengths, so their descriptions and buttons don’t line up. `subgrid` lets each card reuse the parent’s row tracks:" },
      { type: "code", language: "css", code: `.cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
}
.card {
  display: grid;
  grid-row: span 3;              /* title, description, button */
  grid-template-rows: subgrid;   /* share the parent's row sizes */
}` },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Fixed column count everywhere", bad: `.products {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}
/* 4 tiny columns on a phone */`, goodLabel: "Responsive by default", good: `.products {
  display: grid;
  grid-template-columns:
    repeat(auto-fill, minmax(220px, 1fr));
  gap: 1rem;
}` },
      { type: "compare", badLabel: "Wide content blows out 1fr", bad: `.layout {
  display: grid;
  grid-template-columns: 240px 1fr;
}
/* a wide <pre> stretches the 1fr column */`, goodLabel: "Let the track shrink", good: `.layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
}`, text: "`1fr` has an automatic minimum of the content’s min-content size. `minmax(0, 1fr)` lets the column shrink, and the wide content then scrolls inside it." },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "When should you use Grid instead of Flexbox?", answer: ["Use Grid for two-dimensional layouts where you control rows and columns together, such as page structures, dashboards and card grids with aligned columns. Use Flexbox for one-dimensional, content-driven layouts such as navbars, toolbars and button groups. They are often combined, for example a grid page layout with flex navbars inside."] },
      { type: "qa", question: "What is the `fr` unit?", answer: ["A fraction of the free space left in the grid container after fixed-size tracks and gaps are subtracted. `1fr 2fr` gives the second column twice as much of that space as the first."] },
      { type: "qa", question: "Explain `repeat(auto-fill, minmax(250px, 1fr))`.", answer: ["It creates as many columns as fit, each at least 250px wide. Leftover space is shared equally because of `1fr`. The number of columns adapts to the container width without media queries. With `auto-fit`, empty tracks collapse so the existing items stretch to fill the row."] },
      { type: "qa", question: "What does `grid-column: 1 / -1` do?", answer: ["It makes the item span from the first grid line to the last one, so it takes the full width of the explicit grid. Negative line numbers count from the end."] },
      { type: "qa", question: "What is subgrid used for?", answer: ["It lets a nested grid adopt its parent’s row or column tracks, so content inside separate cards, such as titles, descriptions and buttons, aligns across the whole row even when the content lengths differ."] },

      { type: "exercise", title: "Build a dashboard layout", task: "Build a `.dashboard` layout with named areas: a header across the top, a 220px sidebar on the left, a main area, and a 300px “activity” panel on the right. Below 900px, hide the activity panel’s column and stack the sidebar under the header. Use `gap: 1rem`.", language: "css", solution: `.dashboard {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr) 300px;
  grid-template-rows: auto 1fr;
  grid-template-areas:
    "header  header header"
    "sidebar main   activity";
  gap: 1rem;
  min-height: 100dvh;
}
.dashboard__header   { grid-area: header; }
.dashboard__sidebar  { grid-area: sidebar; }
.dashboard__main     { grid-area: main; }
.dashboard__activity { grid-area: activity; }

@media (max-width: 900px) {
  .dashboard {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto;
    grid-template-areas:
      "header"
      "sidebar"
      "main";
  }
  .dashboard__activity { display: none; }
}`, explanation: "The layout is described visually with `grid-template-areas`. At the breakpoint, you redraw the map as a single column and hide the extra panel. `minmax(0, 1fr)` stops wide charts or tables from blowing out the main column." },

      { type: "summary", items: [
        "Grid is a spreadsheet: define columns and rows, and items fill the cells.",
        "`fr` shares free space, and `minmax()` sets limits on a track.",
        "`repeat(auto-fill, minmax(250px, 1fr))` is the responsive card grid without media queries.",
        "Named areas make page layouts readable and easy to rearrange at breakpoints.",
        "Use `minmax(0, 1fr)` to stop overflow, and `subgrid` to align content across cards."
      ] }
    ]
  },

  {
    slug: "positioning-and-z-index",
    title: "Positioning and z-index",
    summary: "Relative, absolute, fixed and sticky positioning, and why z-index: 9999 sometimes still doesn’t work.",
    readTime: 12,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Dropdowns, tooltips, badges on avatars, sticky headers, modals and toasts all depend on positioning. Getting it wrong leads to menus hidden behind other content, headers that don’t stick and the infamous `z-index: 99999` that still loses." },

      { type: "h2", text: "The mental model: sticky notes on a page" },
      { type: "p", text: "Normal elements are printed text on a page, and they flow one after another. A positioned element is a **sticky note**. `relative` nudges the note but leaves its original space reserved. `absolute` lifts it off the page and sticks it relative to the nearest positioned “board”. `fixed` sticks it to the glass of your screen. `sticky` scrolls with the page until it hits an edge, then stays there." },

      { type: "h2", text: "The five position values" },
      { type: "table", headers: ["Value", "In normal flow?", "Positioned relative to", "Typical use"], rows: [
        ["`static` (default)", "Yes", "Not positioned (`top`/`left` are ignored)", "Everything by default"],
        ["`relative`", "Yes, its space is kept", "Its own normal position", "Small nudges, and as an anchor for absolute children"],
        ["`absolute`", "No, its space collapses", "The nearest ancestor that is not `static` (otherwise the page)", "Badges, dropdowns, tooltips, overlays"],
        ["`fixed`", "No", "The viewport", "Floating chat buttons, toasts, fixed navbars"],
        ["`sticky`", "Yes, until it sticks", "Its scroll container, within its parent", "Sticky headers, table headers, sidebars"]
      ] },

      { type: "h2", text: "The relative + absolute pattern" },
      { type: "code", language: "css", code: `.avatar {
  position: relative;       /* becomes the anchor */
  width: 48px;
  height: 48px;
}

.avatar__badge {
  position: absolute;       /* relative to .avatar */
  top: -4px;
  right: -4px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #22c55e;
  border: 2px solid white;
}` },
      { type: "callout", variant: "note", text: "If you forget `position: relative` on the parent, the badge positions itself relative to some ancestor further up, or to the page, and flies off to a corner of the screen." },
      { type: "h3", text: "Full overlay with inset" },
      { type: "code", language: "css", code: `.card { position: relative; }
.card__overlay {
  position: absolute;
  inset: 0;                 /* top: 0; right: 0; bottom: 0; left: 0 */
  background: rgb(0 0 0 / 0.5);
}` },
      { type: "h3", text: "Centring an absolute element" },
      { type: "code", language: "css", code: `.modal {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);   /* shift back by half its own size */
}

/* or, with a known size: */
.modal { position: fixed; inset: 0; margin: auto; width: min(90vw, 480px); height: fit-content; }` },

      { type: "h2", text: "Sticky positioning" },
      { type: "code", language: "css", code: `.table-header th {
  position: sticky;
  top: 0;                 /* stick when it reaches the top */
  background: white;      /* otherwise rows show through */
  z-index: 1;
}

.docs-sidebar {
  position: sticky;
  top: 80px;              /* below a fixed navbar */
  align-self: start;      /* needed inside flex or grid parents */
}` },
      { type: "h3", text: "Why isn’t my sticky element sticking?" },
      { type: "ol", items: [
        "You didn’t set a threshold such as `top: 0`.",
        "An ancestor has `overflow: hidden`, `auto` or `scroll`, which makes that ancestor the scroll container.",
        "The parent is only as tall as the sticky element, so there is no room to stick. It sticks only within its parent.",
        "In a flex or grid parent, the item stretches to full height. Add `align-self: start`."
      ] },

      { type: "h2", text: "z-index and stacking contexts" },
      { type: "p", text: "`z-index` decides which positioned elements appear on top. The catch is that z-index values are only compared **within the same stacking context**. A stacking context is like a sealed folder: everything inside it is layered together and then placed as one unit in its parent." },
      { type: "code", language: "text", code: `<body>
 ├── .header        position: relative; z-index: 10
 │     └── .dropdown   z-index: 9999   ← trapped inside header's context (10)
 └── .hero          position: relative; z-index: 20   ← paints above the dropdown!` },
      { type: "p", text: "The dropdown’s 9999 is compared only with its siblings inside `.header`. At the page level, the whole header counts as 10, which loses to the hero’s 20." },
      { type: "table", headers: ["Creates a new stacking context"], rows: [
        ["`position` other than static, **with** a `z-index` other than `auto`"],
        ["`position: fixed` or `sticky` (always)"],
        ["`opacity` less than 1"],
        ["`transform`, `filter`, `backdrop-filter` or `perspective` (any value except none)"],
        ["`isolation: isolate`"],
        ["Flex or grid children with a `z-index`"]
      ] },
      { type: "callout", variant: "tip", text: "Keep a small z-index scale in variables, for example `--z-dropdown: 100; --z-sticky: 200; --z-modal: 1000; --z-toast: 1100`, and render modals and toasts as direct children of `<body>`. In React, use a portal for this." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "Absolute used for layout", bad: `.sidebar { position: absolute; left: 0; width: 250px; }
.content { position: absolute; left: 250px; }
/* overlaps, doesn't grow, breaks on mobile */`, goodLabel: "Layout tools for layout", good: `.page {
  display: grid;
  grid-template-columns: 250px 1fr;
}` },
      { type: "compare", badLabel: "Escalating z-index", bad: `.dropdown { z-index: 9999; }
.modal    { z-index: 99999; }
.toast    { z-index: 999999; }`, goodLabel: "A deliberate scale", good: `:root {
  --z-dropdown: 100;
  --z-modal: 1000;
  --z-toast: 1100;
}
.modal { z-index: var(--z-modal); }` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between `relative`, `absolute`, `fixed` and `sticky`?", answer: ["`relative` offsets an element from its normal position and keeps its space. `absolute` removes it from the flow and positions it against the nearest positioned ancestor. `fixed` positions it against the viewport so it doesn’t scroll. `sticky` behaves like `relative` until it reaches a threshold such as `top: 0`, then sticks within its scroll container."] },
      { type: "qa", question: "What is the containing block of an absolutely positioned element?", answer: ["The padding box of its nearest ancestor whose `position` isn’t `static`. Ancestors with a `transform`, `filter` or `contain` also count. If there is none, it is the initial containing block, roughly the viewport at the top of the page."] },
      { type: "qa", question: "Why doesn’t `z-index: 9999` bring my element to the front?", answer: ["Because z-index only competes inside the element’s stacking context. If an ancestor creates a stacking context, through z-index, opacity, transform or filter, the element is layered within that ancestor, and the ancestor’s own position among its siblings decides. Fix it by adjusting the ancestor, using `isolation`, or moving the element to `<body>` with a portal."] },
      { type: "qa", question: "Why might `position: sticky` not work?", answer: ["Common causes: no `top` or `bottom` value set, an ancestor with `overflow` other than visible, a parent no taller than the sticky element, or the element stretching inside a flex or grid parent. Add `align-self: start` in that last case."] },
      { type: "qa", question: "Why can a `position: fixed` element stop being fixed to the viewport?", answer: ["If any ancestor has a `transform`, `filter` or `perspective`, that ancestor becomes the containing block, so the fixed element is positioned and scrolls relative to it. This is a common bug inside animated containers."] },

      { type: "exercise", title: "Build a notification badge and dropdown", task: "Create a `.bell` icon button with a red unread `.bell__count` badge in its top-right corner, and a `.bell__menu` dropdown that appears directly below the button, aligned to its right edge, above the rest of the page.", language: "css", solution: `.bell {
  position: relative;          /* anchor for badge and menu */
}

.bell__count {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 999px;
  background: #ef4444;
  color: white;
  font-size: 0.7rem;
  display: grid;
  place-items: center;
}

.bell__menu {
  position: absolute;
  top: calc(100% + 8px);       /* just below the button */
  right: 0;                    /* align right edges */
  width: 280px;
  z-index: var(--z-dropdown, 100);
}`, explanation: "One `position: relative` parent anchors both the badge and the menu. `top: calc(100% + 8px)` places the menu 8px below the button whatever its height. If the menu is still hidden behind something, check the ancestors for stacking contexts." },

      { type: "summary", items: [
        "`relative` nudges and anchors, `absolute` positions against the nearest positioned ancestor, `fixed` uses the viewport, and `sticky` sticks within its container.",
        "The relative-parent, absolute-child pattern powers badges, dropdowns, tooltips and overlays.",
        "`inset: 0` fills the parent, and `translate(-50%, -50%)` centres a positioned element.",
        "z-index only competes within a stacking context, and opacity, transform and filter create new ones.",
        "Use a small z-index scale and portals for modals and toasts."
      ] }
    ]
  },

  {
    slug: "responsive-design",
    title: "Responsive Design",
    summary: "Mobile-first CSS, media and container queries, fluid typography and layouts that adapt to every screen.",
    readTime: 13,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Most visitors, especially in India, come on phones. A site that needs pinching and zooming loses users, and Google ranks pages using their mobile version. Responsive design isn’t a separate mobile site. It is one set of CSS that adapts to any screen." },

      { type: "h2", text: "The mental model: water takes the shape of its glass" },
      { type: "p", text: "Design your content like water: fluid by default, filling whatever container it is poured into. Then add a few **breakpoints**, points where the layout meaningfully changes, such as one column becoming two. Avoid separate fixed designs for “mobile”, “tablet” and “desktop”." },

      { type: "h2", text: "Step 1: the viewport meta tag" },
      { type: "code", language: "html", code: `<meta name="viewport" content="width=device-width, initial-scale=1" />` },
      { type: "p", text: "Without it, mobile browsers pretend the screen is about 980px wide and shrink the whole page, so no media query will behave as you expect." },

      { type: "h2", text: "Step 2: fluid foundations" },
      { type: "code", language: "css", code: `img, video { max-width: 100%; height: auto; }   /* media never overflows */

.container {
  width: min(100% - 2rem, 1200px);   /* 1rem gutter each side, capped at 1200px */
  margin-inline: auto;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: clamp(1rem, 3vw, 2rem);
}` },
      { type: "callout", variant: "tip", text: "`min()`, `max()` and `clamp()` replace many media queries. `minmax(min(100%, 280px), 1fr)` stops a 280px minimum from overflowing a 320px phone." },

      { type: "h2", text: "Step 3: mobile-first media queries" },
      { type: "p", text: "Write base styles for small screens, then add complexity with `min-width` queries as space allows:" },
      { type: "compare", badLabel: "Desktop-first (fight to undo)", bad: `.nav { display: flex; gap: 2rem; }
.sidebar { width: 300px; float: left; }

@media (max-width: 768px) {
  .nav { display: block; }
  .sidebar { width: auto; float: none; }
}`, goodLabel: "Mobile-first (add as you grow)", good: `.nav { display: grid; gap: 0.5rem; }

@media (min-width: 768px) {
  .nav { display: flex; gap: 2rem; }
}

@media (min-width: 1024px) {
  .layout { grid-template-columns: 300px 1fr; }
}` },
      { type: "p", text: "Mobile-first CSS is usually shorter, because small-screen layouts are simpler, and phones don’t have to load and then override desktop rules." },
      { type: "h3", text: "Choosing breakpoints" },
      { type: "p", text: "Don’t design for specific devices. Resize your browser slowly and add a breakpoint where the design starts to look wrong. Common starting points are around 640px, 768px, 1024px and 1280px." },
      { type: "table", headers: ["Query", "Use"], rows: [
        ["`(min-width: 768px)`", "Layout changes for wider screens"],
        ["`(width >= 768px)`", "Modern range syntax, with the same meaning"],
        ["`(hover: hover) and (pointer: fine)`", "Hover effects only on devices with a mouse"],
        ["`(prefers-reduced-motion: reduce)`", "Tone down animations"],
        ["`(prefers-color-scheme: dark)`", "Dark theme"],
        ["`(orientation: landscape)`", "Landscape-specific tweaks"]
      ] },

      { type: "h2", text: "Fluid typography with clamp()" },
      { type: "code", language: "css", code: `h1 {
  /* never smaller than 2rem, never bigger than 3.5rem,
     scales smoothly with the viewport in between */
  font-size: clamp(2rem, 1.2rem + 3vw, 3.5rem);
  line-height: 1.1;
}

p {
  font-size: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
  max-width: 65ch;
}` },
      { type: "callout", variant: "warning", text: "Never use only `vw` for font size, for example `font-size: 4vw`. It ignores the user’s zoom and font settings. Always mix in a `rem` value, as `clamp()` does above." },

      { type: "h2", text: "Container queries: components that adapt to their space" },
      { type: "p", text: "Media queries look at the **screen**. But the same course card might sit in a wide main column or a narrow sidebar. Container queries let the component respond to its **own container’s** width:" },
      { type: "code", language: "css", code: `.card-wrapper {
  container-type: inline-size;   /* make this a query container */
}

.course-card {
  display: grid;
  gap: 1rem;
}

/* when the wrapper is at least 500px wide, go horizontal */
@container (min-width: 500px) {
  .course-card {
    grid-template-columns: 160px 1fr;
  }
}` },

      { type: "h2", text: "Touch-friendly design" },
      { type: "ul", items: [
        "Make tap targets at least **44×44px**, with space between them.",
        "Don’t hide important information behind hover. Touch screens can’t hover.",
        "Use 16px or larger for input text, because iOS zooms into smaller form fields.",
        "Use the right input types so phones show the right keyboard."
      ] },
      { type: "code", language: "css", code: `/* Only add hover effects where hover exists */
@media (hover: hover) and (pointer: fine) {
  .card:hover { transform: translateY(-4px); }
}` },

      { type: "h2", text: "Responsive images and tables" },
      { type: "code", language: "css", code: `.table-wrap { overflow-x: auto; }          /* wide tables scroll inside */
.video      { aspect-ratio: 16 / 9; width: 100%; }
.avatar     { aspect-ratio: 1; object-fit: cover; }` },

      { type: "h2", text: "Testing responsively" },
      { type: "ul", items: [
        "Use DevTools device mode, and also drag the width slowly through every size, not just preset devices.",
        "Test the smallest common width, 320–360px, for overflow.",
        "Test on a real phone. Touch, the on-screen keyboard and browser bars behave differently from DevTools.",
        "Zoom to 200% on desktop. The layout should still work, which is an accessibility requirement."
      ] },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is mobile-first design?", answer: ["Writing the base CSS for the smallest screens and adding layout complexity for larger screens with `min-width` media queries. It usually results in less CSS, simpler overrides and better mobile performance."] },
      { type: "qa", question: "What is the difference between media queries and container queries?", answer: ["Media queries respond to the viewport or device, such as screen width, hover capability or colour scheme. Container queries respond to the size of a specific ancestor container, so a component can adapt to wherever it is placed, for example a card in a sidebar versus the main column."] },
      { type: "qa", question: "Why is the viewport meta tag needed?", answer: ["Without it, mobile browsers render the page at a virtual desktop width (around 980px) and scale it down. With `width=device-width`, the layout viewport matches the device width, so responsive CSS and media queries work as intended."] },
      { type: "qa", question: "How does `clamp()` help responsive design?", answer: ["`clamp(min, preferred, max)` keeps a value fluid within limits. It is often used for typography and spacing, for example `clamp(1rem, 0.9rem + 1vw, 1.5rem)`, which removes the need for several media queries while staying accessible."] },
      { type: "qa", question: "How do you handle hover effects on touch devices?", answer: ["Wrap them in `@media (hover: hover) and (pointer: fine)` so they only apply to devices with a precise pointer, and make sure no essential information or action is only available on hover."] },

      { type: "exercise", title: "Make a course section responsive", task: "Write mobile-first CSS for a `.courses` section: a single column on phones, two columns from 640px, and a featured first card spanning both columns from 640px. The heading should scale fluidly between 1.75rem and 3rem. Hover lift should only apply on mouse devices.", language: "css", solution: `.courses {
  width: min(100% - 2rem, 1100px);
  margin-inline: auto;
}

.courses h2 {
  font-size: clamp(1.75rem, 1.2rem + 2.5vw, 3rem);
}

.courses__grid {
  display: grid;
  gap: 1.25rem;
}

@media (min-width: 640px) {
  .courses__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .course-card--featured { grid-column: 1 / -1; }
}

@media (hover: hover) and (pointer: fine) {
  .course-card { transition: transform 0.25s ease; }
  .course-card:hover { transform: translateY(-4px); }
}`, explanation: "Base styles serve phones, and the `min-width` query adds columns. `clamp()` handles the heading without extra breakpoints, and the hover query keeps touch devices free of sticky hover states. This is close to how the Codeazy course grid itself is built." },

      { type: "summary", items: [
        "Start with the viewport meta tag and fluid foundations: `max-width`, `min()`, `clamp()` and auto-fill grids.",
        "Write mobile-first CSS and add `min-width` breakpoints where the design breaks.",
        "Use `clamp()` with `rem` for fluid, accessible typography.",
        "Use container queries for components that live in different-sized spaces.",
        "Design for touch: 44px targets, no hover-only content, and 16px+ input text."
      ] }
    ]
  },

  {
    slug: "transitions-and-animations",
    title: "Transitions and Animations",
    summary: "Smooth, performant motion with transitions, keyframes and transforms, while respecting users who prefer less motion.",
    readTime: 11,
    content: [
      { type: "h2", text: "Why this matters" },
      { type: "p", text: "Good motion makes interfaces feel responsive and helps users understand changes: a menu sliding in, a button acknowledging a click, a card lifting on hover. Bad motion feels janky, drains batteries and can make some users physically ill. The difference is knowing what to animate and how." },

      { type: "h2", text: "The mental model: from state A to state B" },
      { type: "p", text: "A **transition** is a smooth journey between two states that already exist, such as normal and hover, or closed and open. An **animation** is a choreographed routine of keyframes that can run by itself, loop and have many steps." },
      { type: "table", headers: ["", "Transition", "Animation (@keyframes)"], rows: [
        ["Triggered by", "A state change (hover, a class toggle)", "Applying the animation, which can start immediately"],
        ["Steps", "Two: from and to", "Any number of keyframes"],
        ["Loops", "No", "Yes (`infinite`)"],
        ["Use for", "Hover, focus, open/close", "Loaders, attention cues, entrance effects"]
      ] },

      { type: "h2", text: "Transitions" },
      { type: "code", language: "css", code: `.button {
  background: #6366f1;
  transform: translateY(0);
  transition:
    background-color 200ms ease,
    transform 200ms ease;
}

.button:hover {
  background: #4f46e5;
  transform: translateY(-2px);
}

.button:active {
  transform: translateY(0) scale(0.98);   /* pressed feedback */
}` },
      { type: "table", headers: ["Part", "Example", "Notes"], rows: [
        ["Property", "`transform`", "List specific properties, and avoid `transition: all`"],
        ["Duration", "`200ms`", "UI feedback: 100–300ms. Larger movements: 300–500ms"],
        ["Timing function", "`ease`, `ease-out`, `cubic-bezier(0.22, 1, 0.36, 1)`", "`ease-out` feels responsive for things appearing"],
        ["Delay", "`50ms`", "Useful for staggering lists"]
      ] },

      { type: "h2", text: "What to animate: the performance rule" },
      { type: "p", text: "Browsers render in stages: **layout** (sizes and positions), **paint** (pixels) and **composite** (layering finished pieces on the GPU). Animating properties that trigger layout forces the browser to recalculate the page on every frame." },
      { type: "table", headers: ["Cost", "Properties", "Verdict"], rows: [
        ["Composite only (cheap)", "`transform`, `opacity`", "Animate these"],
        ["Paint", "`color`, `background-color`, `box-shadow`", "OK for small elements"],
        ["Layout (expensive)", "`width`, `height`, `top`, `left`, `margin`, `padding`", "Avoid animating these"]
      ] },
      { type: "compare", badLabel: "Animating layout", bad: `.drawer {
  left: -300px;
  transition: left 300ms;
}
.drawer.open { left: 0; }`, goodLabel: "Animating transform", good: `.drawer {
  transform: translateX(-100%);
  transition: transform 300ms ease-out;
}
.drawer.open { transform: translateX(0); }` },
      { type: "callout", variant: "note", text: "`will-change: transform` hints that an element will animate so the browser can prepare a layer. Use it sparingly, on elements about to animate, not on everything." },

      { type: "h2", text: "Keyframe animations" },
      { type: "code", language: "css", code: `@keyframes fade-up {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.card {
  animation: fade-up 500ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

/* stagger a list */
.card:nth-child(2) { animation-delay: 80ms; }
.card:nth-child(3) { animation-delay: 160ms; }

@keyframes spin {
  to { transform: rotate(360deg); }
}
.spinner {
  width: 24px; height: 24px;
  border: 3px solid #e2e8f0;
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}` },
      { type: "table", headers: ["Property", "Example", "Meaning"], rows: [
        ["`animation-duration`", "`500ms`", "Length of one cycle"],
        ["`animation-timing-function`", "`ease-out`", "Acceleration curve"],
        ["`animation-delay`", "`80ms`", "Wait before starting"],
        ["`animation-iteration-count`", "`infinite`, `3`", "How many times to run"],
        ["`animation-direction`", "`alternate`", "Play forwards, then backwards"],
        ["`animation-fill-mode`", "`both`", "Keep the first keyframe during the delay and the last one after finishing"],
        ["`animation-play-state`", "`paused`", "Pause and resume"]
      ] },

      { type: "h2", text: "Animating height: the classic problem" },
      { type: "p", text: "You can’t transition to `height: auto`. Common solutions:" },
      { type: "code", language: "css", code: `/* 1. Grid rows trick: animate 0fr → 1fr */
.accordion__panel {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 300ms ease;
}
.accordion__panel.open { grid-template-rows: 1fr; }
.accordion__panel > div { overflow: hidden; }

/* 2. For a fixed-height reveal, animate a transform or opacity instead */` },

      { type: "h2", text: "Respect reduced motion" },
      { type: "p", text: "Some users get dizziness or nausea from motion and turn on “reduce motion” in their operating system. Always honour it:" },
      { type: "code", language: "css", code: `@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}` },
      { type: "callout", variant: "tip", text: "Reduced motion doesn’t mean no feedback. Keep colour and opacity changes, and remove large movement, parallax, zooming and auto-playing loops." },

      { type: "h2", text: "Common mistakes" },
      { type: "compare", badLabel: "transition: all", bad: `.card {
  transition: all 0.3s;
}
/* animates everything that changes,
   including layout properties, by accident */`, goodLabel: "Explicit properties", good: `.card {
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}` },
      { type: "compare", badLabel: "Too slow", bad: `.dropdown { transition: opacity 800ms; }
/* the UI feels sluggish */`, goodLabel: "Snappy", good: `.dropdown { transition: opacity 150ms ease-out; }` },

      { type: "h2", text: "Interview questions" },
      { type: "qa", question: "What is the difference between a CSS transition and an animation?", answer: ["A transition interpolates between two states when a property changes, for example on hover or a class toggle. An animation uses `@keyframes` to define any number of steps, can start on its own, loop, alternate and be paused. Use transitions for interaction feedback and animations for loaders and choreographed effects."] },
      { type: "qa", question: "Which CSS properties are cheapest to animate and why?", answer: ["`transform` and `opacity`. They can be handled by the compositor, often on the GPU, without recalculating layout or repainting. Properties such as `width`, `top` or `margin` trigger layout on every frame, which causes jank."] },
      { type: "qa", question: "How do you make animations accessible?", answer: ["Respect `prefers-reduced-motion` by reducing or removing large movement for those users. Avoid flashing content more than three times per second. Never convey information only through motion. Keep durations short and give looping animations a way to pause."] },
      { type: "qa", question: "How can you animate an element to `height: auto`?", answer: ["CSS can’t transition to `auto` directly. Options include animating `grid-template-rows` from `0fr` to `1fr` on a grid wrapper, animating `max-height` to a generous value (which is imprecise), using `transform: scaleY` for simple reveals, or measuring the height with JavaScript."] },

      { type: "exercise", title: "Build an animated toast", task: "Create a `.toast` that slides up and fades in when it gets the class `.toast--visible`, using only cheap properties, with a 250ms ease-out transition. Users who prefer reduced motion should get only the fade, with no sliding.", language: "css", solution: `.toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  opacity: 0;
  transform: translate(-50%, 16px);
  transition:
    opacity 250ms ease-out,
    transform 250ms ease-out;
  pointer-events: none;
}

.toast--visible {
  opacity: 1;
  transform: translate(-50%, 0);
  pointer-events: auto;
}

@media (prefers-reduced-motion: reduce) {
  .toast,
  .toast--visible {
    transform: translate(-50%, 0);   /* no movement */
    transition: opacity 250ms linear; /* fade only */
  }
}`, explanation: "Only `opacity` and `transform` change, so the animation stays smooth. `pointer-events: none` stops the invisible toast from blocking clicks. The reduced-motion block keeps the toast in its final position and leaves only the fade." },

      { type: "summary", items: [
        "Transitions move between two states, and keyframe animations choreograph many steps.",
        "Animate `transform` and `opacity`. Avoid animating layout properties such as width, top or margin.",
        "List transition properties explicitly, and keep UI motion between 100 and 300ms.",
        "Use the `0fr` → `1fr` grid trick to animate height to its natural size.",
        "Always honour `prefers-reduced-motion`."
      ] }
    ]
  }
];
